import { useQueryClient } from '@tanstack/react-query';
import { VAT_RATE_LABELS, VAT_RATES, type CategoryDTO, type ProductDTO, type VatRate } from '@bifeai/shared';
import { Badge, Button, Callout, Dialog, Field, Input, Select, useToast } from '@bifeai/ui';
import { Percent } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { useCategories, useProducts } from '../lib/catalog';

type Mode = 'category' | 'products' | 'missing';

/**
 * Cambiar el IVA de muchos productos de una vez: una categoría entera (por ejemplo, Elaborados al 21 %),
 * los productos que se tilden, o todos los que todavía no tienen IVA. Pisa el IVA que tuvieran.
 */
export function VatDialog({ onClose, initialIds = [] }: { onClose: () => void; initialIds?: string[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const categories = useCategories();
  const products = useProducts({ includeInactive: true });
  const [mode, setMode] = useState<Mode>(initialIds.length ? 'products' : 'category');
  const [rate, setRate] = useState<VatRate>('21');
  const [categoryId, setCategoryId] = useState('');
  const [picked, setPicked] = useState<Set<string>>(() => new Set(initialIds));
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const all = useMemo(() => (products.data ?? []).filter((p) => !p.isCarcass), [products.data]);
  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const shown = all.filter((p) => !search.trim() || norm(`${p.name} ${p.categoryName ?? ''}`).includes(norm(search.trim())));
  const missing = all.filter((p) => !p.vatRate).length;
  const cat: CategoryDTO | undefined = categories.data?.find((c) => c.id === categoryId);
  const inCategory = (p: ProductDTO) => (categoryId === 'none' ? !p.categoryId : p.categoryId === categoryId);
  const count = mode === 'category' ? (categoryId ? all.filter(inCategory).length : 0) : mode === 'products' ? picked.size : missing;

  async function apply() {
    if (!count)
      return setError(
        mode === 'category' ? 'Elegí una categoría.' : mode === 'products' ? 'Tildá al menos un producto.' : 'No hay productos sin IVA.',
      );
    setBusy(true);
    setError(null);
    try {
      const body =
        mode === 'category'
          ? { vatRate: rate, categoryId: categoryId === 'none' ? null : categoryId }
          : mode === 'products'
            ? { vatRate: rate, productIds: [...picked] }
            : { vatRate: rate, onlyMissing: true };
      const r = await api.post<{ updated: number }>('/fiscal/vat', body);
      await Promise.all([qc.invalidateQueries({ queryKey: ['products'] }), qc.invalidateQueries({ queryKey: ['fiscal'] })]);
      toast.success('IVA actualizado', `${r.updated} producto${r.updated === 1 ? '' : 's'} con ${VAT_RATE_LABELS[rate]}.`);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const toggle = (id: string) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title="Cambiar el IVA"
      description="Elegí a qué productos y qué alícuota. Se pisa el IVA que tuvieran. Consultá con tu contador la alícuota de cada producto."
      wide
    >
      <div className="bf-form">
        <div className="bf-view-switch" role="tablist" aria-label="A qué productos">
          {(
            [
              ['category', 'Una categoría'],
              ['products', 'Elegir productos'],
              ['missing', `Los que no tienen IVA (${missing})`],
            ] as const
          ).map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={mode === k} onClick={() => setMode(k)}>
              {l}
            </button>
          ))}
        </div>

        <div className="bf-form-row">
          {mode === 'category' && (
            <Field label="Categoría">
              <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">Elegí una categoría</option>
                {categories.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({all.filter((p) => p.categoryId === c.id).length})
                  </option>
                ))}
                <option value="none">Sin categoría ({all.filter((p) => !p.categoryId).length})</option>
              </Select>
            </Field>
          )}
          <Field label="Alícuota">
            <Select value={rate} onChange={(e) => setRate(e.target.value as VatRate)}>
              {VAT_RATES.map((r) => (
                <option key={r} value={r}>
                  {VAT_RATE_LABELS[r]}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {mode === 'category' && cat && (
          <p className="bf-hint" style={{ margin: 0 }}>
            Se cambia el IVA de los {count} productos de {cat.name}, aunque ya tengan otro cargado.
          </p>
        )}

        {mode === 'products' && (
          <>
            <Input placeholder="Buscar producto o categoría" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Buscar" />
            <div className="bf-vat-pick">
              {shown.map((p) => (
                <label key={p.id} className="bf-vat-pick-row">
                  <input type="checkbox" checked={picked.has(p.id)} onChange={() => toggle(p.id)} />
                  <span className="bf-cell-main">{p.name}</span>
                  <span className="bf-cell-sub">{p.categoryName ?? 'Sin categoría'}</span>
                  {p.vatRate ? <Badge>{VAT_RATE_LABELS[p.vatRate]}</Badge> : <Badge tone="warning">Sin IVA</Badge>}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button size="sm" onClick={() => setPicked((s) => new Set([...s, ...shown.map((p) => p.id)]))}>
                Tildar los {shown.length} de la lista
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPicked(new Set())}>
                Destildar todos
              </Button>
            </div>
          </>
        )}

        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" icon={<Percent />} loading={busy} disabled={!count} onClick={() => void apply()}>
            Poner {VAT_RATE_LABELS[rate]} a {count} producto{count === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
